import { Circle, TrendingUp, Users } from "lucide-react";
import { useEffect, useState } from "react";
import { Col } from "../../../../../components/col";
import { Row } from "../../../../../components/row";
import LocationInsight from "../../LocationInsight";
import HeatMap from "../../map/user-distribution/HeatMap";
import { MapBodySkeleton, SidePanelSkeleton } from "../../region/region-skeletons";
import { RegionState } from "../../region/RegionState";
import type { AreaModel } from "../../../types/Area";
import type { OrganizationMapConfig } from "../../../commons/Constant";

type UserDistributionTabProps = {
  areas: AreaModel[] | null;
  organizationName: string;
  mapConfig?: OrganizationMapConfig;
  isLoading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  filterRevision: number;
};

export default function UserDistributionTab({
  areas,
  organizationName,
  mapConfig,
  isLoading = false,
  error,
  onRetry,
  filterRevision,
}: UserDistributionTabProps) {
  const [selectedDistrict, setSelectedDistrict] = useState<AreaModel | null>(null);

  useEffect(() => {
    setSelectedDistrict(null);
  }, [filterRevision]);

  return (
    <Row className="h-[600px] items-stretch">
      <div className="w-9/12 border border-gray-200 dark:border-gray-700 rounded-2xl bg-white dark:bg-gray-800 flex flex-col">
        <Row className="px-5 pt-5 pb-5 justify-between items-center">
          <Col className="gap-1 text-sm">
            <div className="flex">
              <Users
                className="mr-1"
                color="#00C49A"
              />
              <span className="text-gray-900 dark:text-gray-100">
                {organizationName} - CivicPoints User Distribution
              </span>
            </div>
            <div className="text-gray-600 dark:text-gray-400">
              Interactive geographic distribution of CivicPoints users across city districts
            </div>
          </Col>
        </Row>
        <RegionState
          isLoading={isLoading}
          error={error}
          onRetry={onRetry}
          skeleton={<MapBodySkeleton />}
        >
          <div className="relative rounded-2xl flex-1">
            <div className="absolute top-6 right-6 bg-white dark:bg-gray-800 shadow-md border border-gray-200 dark:border-gray-600 rounded-2xl px-4 py-3 text-sm z-[9]">
              <div className="font-semibold mb-2 flex items-center text-[#00BCD4] dark:text-cyan-400 text-[12px]">
                <TrendingUp
                  size={16}
                  className="mr-1"
                />
                <span> Adoption Levels</span>
              </div>
              <div className="flex items-center gap-2 mb-1">
                <span className="w-3 h-3 rounded-full bg-green-500" />
                <span className="text-[12px] text-gray-700 dark:text-gray-300">High (&gt;70%)</span>
              </div>
              <div className="flex items-center gap-2 mb-1">
                <span className="w-3 h-3 rounded-full bg-orange-500" />
                <span className="text-[12px] text-gray-700 dark:text-gray-300">
                  Medium (50%-70%)
                </span>
              </div>
              <div className="flex items-center gap-2 mb-1">
                <span className="w-3 h-3 rounded-full bg-red-500" />
                <span className="text-[12px] text-gray-700 dark:text-gray-300">Low (&lt;50%)</span>
              </div>
              <hr className="my-2 border-gray-200 dark:border-gray-600" />
              <span className="text-[12px] text-gray-500 dark:text-gray-400">
                Click markers for details
              </span>
            </div>
            <div className="absolute bottom-6 left-6 bg-white dark:bg-gray-800 shadow-md border-2 border-gray-200 dark:border-gray-600 rounded-xl px-2 py-1 text-[10px] z-[9999]">
              <div className="flex justify-left items-center">
                <Circle
                  className="mr-2"
                  size={12}
                  fill="#00BCD4"
                  stroke="#00BCD4"
                />
                <div>
                  <span className="text-[#00BCD4] dark:text-cyan-400 text-sm block">
                    {organizationName}
                  </span>
                  <span className="block text-gray-700 dark:text-gray-300">
                    Shelby County, Tennessee
                  </span>
                </div>
              </div>
            </div>
            <HeatMap
              areas={areas}
              mapConfig={mapConfig}
              selectedDistrict={selectedDistrict}
              filterRevision={filterRevision}
              onSelectDistrict={setSelectedDistrict}
            />
          </div>
        </RegionState>
      </div>
      <RegionState
        isLoading={isLoading}
        error={error}
        onRetry={onRetry}
        skeleton={<SidePanelSkeleton />}
      >
        <div className="w-3/12 h-full border border-gray-200 dark:border-gray-700 rounded-2xl bg-white dark:bg-gray-800 flex flex-col">
          <div className="w-full flex items-center p-5">
            <span className="text-md text-[#364153] dark:text-gray-200">Location Insights</span>
          </div>
          <div className="flex-1 mt-1 overflow-y-auto">
            <LocationInsight location={selectedDistrict} organizationName={organizationName} />
          </div>
        </div>
      </RegionState>
    </Row>
  );
}
